<template>
  <div
    class="template-card"
    @click="handleChooseTemplate"
    @mouseover="handleHoverTemplate"
  >
    <div v-if="$slots.thumbnail">
      <slot name="thumbnail" />
    </div>
    <div
      :class="[
        'template-thumbnail',
        {
          'has-thumbnail': hasThumbnail,
        }
      ]"
      v-else
    >
      <!-- use data-src for lazy-loading. Check initLazyLoad() -->
      <div
        v-if="hasThumbnail"
        class="object-cover w-full h-full"
      >
        <!-- use v-show here, not v-if for <img> or lazyload will break -->
        <img
          v-show="isThumbnailLoaded"
          class="thumbnail-image-with-skeleton"
          @load="onThumbnailLoad"
          :src="getThumbnailUrl"
          alt="thumbnail"
        >
        <aha-skeleton-loader
          v-show="!isThumbnailLoaded"
          width="100%"
          height="100%"
        />
      </div>
      <img
        class="object-cover w-full h-full thumbnail-image"
        v-else
        :src="defaultThumbnail"
        alt="Thumbnail default"
      />
    </div>
    <div class="flex w-full p-2 pb-4">
      <img
        v-if="isOfficial"
        class="mr-2"
        :src="require('@/assets/images/icon/aha-official-avt.svg')"
      >
      <aha-avatar
        v-else
        class="mt-1 mr-2"
        :name="getAuthorName"
        :image="getAuthorAvt"
        :width="28"
        :height="28"
      />
      <div class="flex-wrap inline-flex pr-10 w-full max-w-52">
        <b class="template-name w-full" :title="presentation.name">
          {{ presentation.name }}
        </b>
        <span class="template-author w-full">
          <img
            v-if="isOfficial"
            class="inline mr-0.5"
            :src="require('@/assets/images/icon/author-checked.svg')"
          >
          <span class="mt-0.5 truncate">
            {{ isOfficial ? 'AhaSlides Official' : getAuthorName }}
          </span>
        </span>
      </div>
    </div>
    <div class="template-bottom">
      <span class="information flex pt-2 pb-2">
        <p class="inline-flex items-center mr-4">
          <span class="icon-container">
            <aha-icon
              class="icon-slides"
              name="slides"
              width="10"
              height="10"
              viewBox="0 0 12 12"
              v-tooltip="'Number of slides'"
            />
          </span>
          {{ presentation.slideCount }}
        </p>
        <p class="inline-flex items-center mr-4 container-item-bottom">
          <span class="icon-container">
            <aha-icon
              class="icon-download-2 text-secondary-50"
              name="download-2"
              viewBox="0 0 14 16"
              width="9"
              v-tooltip="'Number of downloads'"
            />
          </span>
          {{ timesCopied | formatNumber }}
        </p>
      </span>
      <div class="items-center">
        <aha-button
          class="get-template-btn aha-button-base"
          :class="{'hovering': !!copyingTemplateId && copyingTemplateId === presentation.id}"
          @click.stop="getTemplate(presentation)"
          :submitting="!!copyingTemplateId && copyingTemplateId === presentation.id"
        >
          Get
        </aha-button>
      </div>
    </div>
    <TemplatePreviewPopup
      v-if="isShowingTemplatePreviewPopup"
      @hide="toggleTemplatePreviewPopup"
      :presentationShareCode="presentation.shareCode"
      :templateLocation="templateLocation"
      :where="where"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Utils from '@/util'
import gaCategories from '@/constant/gaCategories'
import TemplatePreviewPopup from '@/components-v2/template/TemplatePreviewPopup.vue'

const defaultThumbnail = require('@/assets/images/icon/default-thumbnail.svg')

export default {
  components: {
    TemplatePreviewPopup,
  },
  props: {
    size: {
      type: String,
      default: 'md',
    },
    presentation: {
      type: Object,
      required: true,
    },
    customHandler: {
      type: Function,
      required: false,
      default: null,
    },
    templateLocation: {
      type: String,
      default: null,
    },
    where: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      defaultThumbnail,
      isThumbnailLoaded: false,
      isShowingTemplatePreviewPopup: false,
    }
  },
  computed: {
    ...mapGetters('presentationTemplates', [
      'copyingTemplateId',
    ]),

    hasThumbnail() {
      return this.presentation.thumbnailImage || this.presentation.customThumbnailImage
    },
    isOfficial() {
      return this.presentation.User && this.presentation.User.type === 'official'
    },
    numOfSlides() {
      return this.presentation.slideCount || '0'
    },
    timesCopied() {
      return this.presentation.copyCount || '0'
    },
    getAuthorName() {
      if (this.presentation.User) {
        return `${this.presentation.User.firstName} ${this.presentation.User.lastName}`
      }
      return ''
    },
    getAuthorAvt() {
      if (this.presentation.User) {
        return this.presentation.User.avatar
      }
      return ''
    },
    getThumbnailUrl() {
      if (this.presentation.customThumbnailImage) {
        return this.presentation.customThumbnailImage
      }
      return this.presentation.thumbnailImage
    },
  },
  methods: {
    ...mapActions('presentationTemplates', ['copyFromTemplateAction']),
    toggleTemplatePreviewPopup() {
      this.isShowingTemplatePreviewPopup = !this.isShowingTemplatePreviewPopup
      if (this.isShowingTemplatePreviewPopup) {
        document.body.classList.add('no-scroll-template')
        Utils.gaTrackEvent(gaCategories.TEMPLATE_LIBRARY, 'Preview template', this.where, this.presentation.id)
      } else {
        document.body.classList.remove('no-scroll-template')
      }
    },
    onThumbnailLoad() {
      this.isThumbnailLoaded = true
    },
    handleCopyTemplateSuccess(presentation) {
      const newPresentationId = presentation.id
      const params = { id: newPresentationId }
      this.$router.push({ name: 'PresentationDetail', params })
      Utils.gaTrackEvent(gaCategories.TEMPLATE_LIBRARY, 'Create new presentation', 'Use template', null, {
        sourcePresentationId: this.presentation.id,
        presentationId: newPresentationId,
      })
    },
    handleChooseTemplate() {
      if (this.customHandler) {
        this.customHandler()
        return
      }
      this.toggleTemplatePreviewPopup()
    },
    handleHoverTemplate() {
      Utils.gaTrackEvent(gaCategories.TEMPLATE_LIBRARY, 'Templates > Hover', this.where, this.presentation.id)
    },
    goToLiveDemo() {
      this.$router.push({ name: 'PresentationPlayDemo', query: { id: this.presentation.id } })
      Utils.gaTrackEvent(gaCategories.TEMPLATE_LIBRARY, 'User clicks demo on template', this.presentation.id)
    },
    getTemplate(templateData) {
      Utils.gaTrackEvent('Template Library > Listing', 'Get template', this.where, templateData.id)
      this.copyFromTemplateAction({
        template: templateData,
        callback: this.handleCopyTemplateSuccess,
        isTracking: false,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
  $card-width: 240px;
  $thumbnail-ratio: calc(16 / 9);
  .template-card {
    @apply rounded-lg shadow-card cursor-pointer;
    @apply transition-colors;
    background: white;
    width: $card-width;
    // display: inline-block; can't use this: see: https://ahaslides.atlassian.net/browse/AHA-11566 for more detail
    display: inline-grid;
    margin-right: 24px;
    position: relative;
    &:hover {
      .template-thumbnail {
        @apply border-primary-90;
      }
      .get-template-btn {
        background: rgba(52, 142, 246, 1);
        color: rgba(255, 255, 255, 1);

        &:active:not(:disabled) {
          @apply bg-primary-100;
        }
        @apply bg-primary-90;
        @apply shadow-blue-md;

        &:hover:not(:active):not(:disabled) {
          @apply shadow-blue;
        }
      }
    }
    .template-author {
      align-items: center;
      display: flex;
    }
    .spinner {
      width: auto;
    }
    .get-template-btn {
      background: rgba(243, 244, 247, 1);
      min-width: 5rem;
      &.hovering {
          background: rgba(52, 142, 246, 1);
          color: rgba(255, 255, 255, 1);

          &:active:not(:disabled) {
            @apply bg-primary-100;
          }
          @apply bg-primary-90;
          @apply shadow-blue-md;

          &:hover:not(:active):not(:disabled) {
            @apply shadow-blue;
          }
        }
    }
  }
  .template-thumbnail {
    @apply cursor-pointer relative mb-2 flex items-center justify-center rounded-xl;
    width: $card-width;
    height: calc(#{$card-width} / #{$thumbnail-ratio});
    .thumbnail-image {
      border-top-left-radius: 0.5rem;
      border-top-right-radius: 0.5rem;
    }
    .thumbnail-image-with-skeleton {
      border-top-left-radius: 0.5rem;
      border-top-right-radius: 0.5rem;
      width: 100%;
      height: 100%;
    }
  }

  .template-name {
    word-break: break-word;
    overflow: hidden;
    line-height: 1.125rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .template-bottom {
    display: inline-flex;
    margin: 0 16px 16px;
    .information {
      width: 75%;
      color: rgba(49, 49, 49, 1);
      .container-item-bottom {
        margin-right: 8px;
      }
      .icon-container {
        position: relative;
        width: 16px;

        svg {
          width: 12px;
          height: 12px;
          display: inline-flex;
          vertical-align: baseline;
          color: rgba(49, 49, 49, 1);
        }
      }
    }
  }

  .object-cover {
    border-radius: 8px 8px 0px 0px;
  }
</style>
